import type { CSSProperties } from 'react';
import React from 'react';
import routesConfig from 'constants/routesConfig.json';
import demoTourSteps from 'productTours/demoTour/demoTourSteps';
import onboardingTourSteps from 'productTours/onboardingTour/onboardingTourSteps';

// hooks
import { useUser } from 'shared/hooks/api/user';

// styles
import breakpoints from 'theme/atoms/breakpoints';

// Define a History interface if it's not available
interface History {
  push: (path: string | { search: string }) => void;
  replace: (path: string | { [key: string]: string }) => void;
}

interface TourStep {
  position?: 'center' | 'top' | 'right' | 'bottom' | 'left';
  selector?: string;
  content: React.ReactNode;
  action?: (element?: Element) => void;
  styles?: {
    maskArea?: (base: CSSProperties) => CSSProperties & { rx?: number };
    [key: string]:
      | ((base: CSSProperties) => CSSProperties & { rx?: number })
      | undefined;
  };
  stepInteraction?: boolean;
  disableWhenSelectorFalsy?: boolean;
}

export default function useTourConfig(history: History): TourStep[] {
  const { data: user } = useUser();

  const hideHighlight = {
    maskArea: (base: CSSProperties): CSSProperties & { rx?: number } => ({
      ...base,
      display: 'none',
    }),
  };

  const hideControls = {
    controls: (base) => ({
      ...base,
      justifyContent: 'center',
    }),
    arrow: () => ({
      display: 'none', // Hides the navigation arrows
    }),
  };

  const showSidePanel = (show?: boolean) => {
    // Check if we're on desktop (window width > tablet breakpoint)
    const isDesktop = window.innerWidth > parseInt(breakpoints.tablet, 10);

    // On desktop during tours, only allow opening the panel, not closing it
    if (isDesktop && show === false) {
      return; // Don't close the panel on desktop during tours
    }

    history.replace(
      `${routesConfig.ALL_CONVOS_PAGE}?showpanel=${show}&tour=true`
    );
  };

  return user?.is_demo
    ? demoTourSteps(hideHighlight, showSidePanel)
    : onboardingTourSteps(hideControls, hideHighlight, showSidePanel);
}
